"use client"

import { useState, useEffect, useRef, useCallback, useContext } from "react"
import { PlanContext } from "../App"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faSyncAlt,
  faDownload,
  faTrashAlt,
  faFileCsv,
  faSearch,
  faExchangeAlt,
  faShoppingCart,
  faSpinner, // Import faSpinner for loading indicator
} from "@fortawesome/free-solid-svg-icons"
import downloadProductsCSV from "../components/downloadProductsCSV"
import { handleCSVConversion } from "../components/csvConverter"

const ExportControls = ({
  exportType = "Collections",
  onExportTypeChange,
  onPlatformChange,
  onSearchChange,
  onClear,
  totalItems,
  websiteUrl,
  isTotalItemsLoading, // New prop to indicate if total items are loading
}) => {
  const [selectedExportType, setSelectedExportType] = useState(exportType)
  const [exportPlatform, setExportPlatform] = useState("Shopify")
  const [searchQuery, setSearchQuery] = useState("")
  const [displayedTotal, setDisplayedTotal] = useState(0)
  const animationFrameRef = useRef(null)
  const [conversionType, setConversionType] = useState("")
  const [conversionFile, setConversionFile] = useState(null)
  const fileInputRef = useRef(null)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const plan = useContext(PlanContext)
  const isExportAllDisabled = plan === "Free" || plan === "trial"

  useEffect(() => {
    setSelectedExportType(exportType)
  }, [exportType])

  useEffect(() => {
    // Update displayedTotal only when totalItems is not loading
    // and when totalItems actually changes
    if (!isTotalItemsLoading) {
      setDisplayedTotal(totalItems)
    }
  }, [totalItems, isTotalItemsLoading]) // Added isTotalItemsLoading as a dependency here

  const handleExportTypeChange = useCallback(
    (e) => {
      const value = e.target.value
      setSelectedExportType(value)
      if (onExportTypeChange) onExportTypeChange(value)
    },
    [onExportTypeChange],
  )

  const handlePlatformChange = useCallback(
    (e) => {
      const value = e.target.value
      setExportPlatform(value)
      if (onPlatformChange) onPlatformChange(value)
    },
    [onPlatformChange],
  )

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchQuery(value)
      if (onSearchChange) onSearchChange(value)
    },
    [onSearchChange],
  )

  const handleExportAllClick = useCallback(() => {
    downloadProductsCSV(websiteUrl, exportPlatform, "Products")
  }, [websiteUrl, exportPlatform])

  const handleFreeExportClick = useCallback(() => {
    downloadProductsCSV(websiteUrl, exportPlatform, "Products", 50)
  }, [websiteUrl, exportPlatform])

  const handleConversionTypeChange = useCallback((e) => {
    const value = e.target.value
    setConversionType(value)
    setConversionFile(null)
  }, [])

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0]
    setConversionFile(file)
  }, [])

  const handleConvertFile = useCallback(() => {
    if (!conversionType) {
      // Replaced alert with a more user-friendly message or modal in a real app
      // For this example, we'll use console.error
      console.error("Please select a conversion type.");
      return;
    }
    if (!conversionFile) {
      // Replaced alert with a more user-friendly message or modal in a real app
      console.error("Please upload a CSV file for conversion.");
      return;
    }
    handleCSVConversion(conversionFile, conversionType)
  }, [conversionType, conversionFile])

  return (
    <div className="export-controls-container">
      <style jsx="true">{`
        .export-controls-container {
          padding: 2rem 0;
          max-width: 100%;
          margin: 1.5rem auto;
        }
        
        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 1rem;
          text-align: center;
        }
        
        .badge {
          display: inline-block;
          background-color: #007BFF;
          color: white;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 9999px;
          margin-left: 0.5rem;
          font-weight: 600;
        }
        
        .dropdown-container {
          display: flex;
          justify-content: center;
          margin-bottom: 1.5rem;
        }
        
        .dropdown {
          padding: 0.75rem 1rem;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          background-color: #fff;
          outline: none;
          width: 100%;
          max-width: 300px;
          font-size: 1rem;
          transition: all 0.2s ease;
        }
        
        .dropdown:focus {
          border-color: #007BFF;
          box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
        }
        
        .button-container {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin: 1.5rem 0;
          flex-wrap: wrap;
        }
        
        .action-button {
          padding: 0.75rem 1.25rem;
          border-radius: 0.375rem;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
          transition: all 0.2s ease;
          justify-content: center;
        }
        
        .clear-button {
          background-color: #6b7280;
          color: white;
        }
        
        .clear-button:hover {
          background-color: #4b5563;
        }
        
        .free-button {
          background-color: #007BFF;
          color: white;
        }
        
        .free-button:hover {
          background-color: #008f85;
        }
        
        .export-button {
          background-color: #f33a3a;
          color: white;
        }
        
        .export-button:hover {
          background-color: #e02e2e;
        }

        .export-button:disabled {
          background-color: #f33a3a99;
          cursor: not-allowed;
          position: relative;
        }

        .upgrade-tooltip {
          display: none;
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #333;
          color: white;
          padding: 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          white-space: nowrap;
          z-index: 10;
          margin-bottom: 0.5rem;
        }

        .export-button:disabled:hover .upgrade-tooltip {
          display: block;
        }
        
        .conversion-section {
          margin-top: 2.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }
        
        .conversion-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 1rem;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .file-upload-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .file-label {
          padding: 0.75rem 1.25rem;
          border: 1px dashed #d1d5db;
          border-radius: 0.375rem;
          background-color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
          width: 100%;
          max-width: 300px;
          justify-content: center;
        }
        
        .file-label:hover {
          border-color: #007BFF;
          background-color: #f0fdfa;
        }
        
        .convert-button {
          background-color: #f59e0b;
          color: white;
          padding: 0.75rem 1.25rem;
          border-radius: 0.375rem;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .convert-button:hover {
          background-color: #d97706;
        }
        
        .info-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding: 1rem 0;
          border-top: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .info-part {
          flex: 1;
          min-width: 200px;
        }
        
        .total-items {
          font-size: 1.25rem;
          font-weight: 700;
          color: #007BFF;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .total-items .spinner {
          font-size: 1rem; /* Smaller spinner for inline display */
          margin-left: 0.5rem;
          color: #007BFF;
        }
        
        .platform-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .platform-label {
          font-weight: 600;
          color: #4b5563;
        }
        
        .search-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .search-icon {
          position: absolute;
          left: 0.75rem;
          color: #9ca3af;
        }
        
        .search-input {
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          outline: none;
          width: 100%;
          max-width: 250px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }
        
        .search-input:focus {
          border-color: #007BFF;
          box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
        }
        
        .file-name {
          font-size: 0.875rem;
          color: #4b5563;
          margin-top: 0.5rem;
          text-align: center;
          word-break: break-all;
          max-width: 300px;
        }
        
        @media (max-width: 768px) {
          .info-container {
            flex-direction: column;
            align-items: stretch;
          }
          
          .info-part {
            text-align: center;
          }
          
          .button-container {
            flex-direction: column;
            align-items: center;
          }
          
          .action-button {
            width: 100%;
            max-width: 300px;
          }
        }
      `}</style>

      <h2 className="section-title">
        Export Controls
        <span className="badge">Step 2</span>
      </h2>

      {/* Export Type Dropdown */}
      <div className="dropdown-container">
        <select value={selectedExportType} onChange={handleExportTypeChange} className="dropdown">
          <option>Collections</option>
          <option>Products</option>
          <option>Massive Data</option>
        </select>
      </div>

      {/* Action Buttons */}
      <div className="button-container">
        <button className="action-button clear-button" onClick={onClear}>
          <FontAwesomeIcon icon={faTrashAlt} /> Clear
        </button>
        <button className="action-button free-button" onClick={handleFreeExportClick}>
          <FontAwesomeIcon icon={faSyncAlt} /> Free Export
        </button>
        <button
          className="action-button export-button"
          onClick={handleExportAllClick}
          disabled={isExportAllDisabled}
          title={isExportAllDisabled ? "Upgrade plan to export all products" : "Export all products"}
          style={isExportAllDisabled ? { position: "relative" } : {}}
        >
          <FontAwesomeIcon icon={faDownload} /> Export All
          {isExportAllDisabled && <div className="upgrade-tooltip">Upgrade plan to export all products</div>}
        </button>
      </div>

      {/* Info Container */}
      <div className="info-container">
        <div className="info-part">
          <p className="total-items">
            <FontAwesomeIcon icon={faShoppingCart} />
            {selectedExportType ? selectedExportType.toUpperCase() : "COLLECTIONS"}:{" "}
            {isTotalItemsLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin className="spinner" />
            ) : (
              displayedTotal
            )}
          </p>
        </div>

        <div className="info-part">
          <div className="platform-container">
            <span className="platform-label">Export To:</span>
            <select value={exportPlatform} onChange={handlePlatformChange} className="dropdown">
              <option>Shopify</option>
              <option>WooCommerce</option>
            </select>
          </div>
        </div>

        <div className="info-part">
          <div className="search-container">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
          </div>
        </div>
      </div>

      {/* Conversion Section */}
      <div className="conversion-section">
        <h3 className="conversion-title">
          <FontAwesomeIcon icon={faExchangeAlt} />
          Format Conversion
        </h3>

        <div className="dropdown-container">
          <select value={conversionType} onChange={handleConversionTypeChange} className="dropdown">
            <option value="">Select Conversion Type</option>
            <option value="woo-to-shopify">WooCommerce to Shopify</option>
            <option value="shopify-to-woo">Shopify to WooCommerce</option>
          </select>
        </div>

        {conversionType && (
          <div className="file-upload-container">
            <label htmlFor="conversionFile" className="file-label">
              <FontAwesomeIcon icon={faFileCsv} />
              {conversionFile ? "Change File" : "Select CSV File"}
            </label>
            <input
              type="file"
              id="conversionFile"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
              ref={fileInputRef}
            />

            {conversionFile && (
              <>
                <div className="file-name">{conversionFile.name}</div>
                <button className="convert-button" onClick={handleConvertFile}>
                  <FontAwesomeIcon icon={faExchangeAlt} /> Convert File
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ExportControls